import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

const CacheContext = createContext(null);

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

export const CacheProvider = ({ children, auth }) => {
  // State for caching
  const [cache, setCache] = useState({
    profileStats: null,
    calendarEntries: null,
    currentChallenge: null,
    challenges: {}, // Map of "YYYY-MM" -> challenge data
    communityFeed: null, // { entries: [], page: 0, hasMore: true }
    leaderboardOverall: null,
    leaderboardMonthly: {}, // Map of "YYYY-MM" -> leaderboard data
    userProfiles: {}, // Map of userId -> { profile, entries, stats }
  });

  // Helper to update specific cache key
  const updateCache = useCallback((key, value) => {
    setCache(prev => ({ ...prev, [key]: value }));
  }, []);

  // Invalidate cache keys (can be single key or array of keys)
  const invalidate = useCallback((keys) => {
    const keysToInvalidate = Array.isArray(keys) ? keys : [keys];
    setCache(prev => {
      const newCache = { ...prev };
      keysToInvalidate.forEach(key => {
        newCache[key] = null;
        // special handling for maps
        if (key === 'challenges') newCache.challenges = {};
        if (key === 'leaderboardMonthly') newCache.leaderboardMonthly = {};
        if (key === 'userProfiles') newCache.userProfiles = {};
      });
      return newCache;
    });
  }, []);

  // --- Fetchers with Caching ---

  const fetchProfileStats = useCallback(async (userId) => {
    if (cache.profileStats) return cache.profileStats;
    try {
      const response = await axios.get(`${API}/users/${userId}/stats`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      updateCache('profileStats', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch profile stats', error);
      throw error;
    }
  }, [auth.token, cache.profileStats, updateCache]);

  const fetchCalendarEntries = useCallback(async (force = false) => {
    if (!force && cache.calendarEntries) return cache.calendarEntries;
    try {
      const response = await axios.get(`${API}/entries/me`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      updateCache('calendarEntries', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch entries', error);
      throw error;
    }
  }, [auth.token, cache.calendarEntries, updateCache]);

  const fetchCurrentChallenge = useCallback(async () => {
    // We can persist this one potentially, but let's keep in memory for now
    if (cache.currentChallenge) return cache.currentChallenge;
    try {
      const response = await axios.get(`${API}/challenges/current`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      updateCache('currentChallenge', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch current challenge', error);
      throw error;
    }
  }, [auth.token, cache.currentChallenge, updateCache]);

  const fetchChallengeForMonth = useCallback(async (year, month) => {
    const key = `${year}-${month}`;
    if (cache.challenges[key]) return cache.challenges[key];
    try {
      const response = await axios.get(`${API}/challenges/${year}/${month}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setCache(prev => ({
        ...prev,
        challenges: { ...prev.challenges, [key]: response.data }
      }));
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch challenge for ${year}-${month}`, error);
      // Don't throw if just not found, return null
      return null;
    }
  }, [auth.token, cache.challenges]);

  const fetchCommunityFeed = useCallback(async (skip = 0, limit = 10, reset = false) => {
    // If we have cached feed and we are asking for page 0 (or already have data) AND NOT resetting
    // But pagination logic is complex to cache perfectly if we just append.
    // Simplest approach: Cache the *entire* list so far.
    // If reset is true, we ignore cache.
    // If skip > 0, we assume we are appending.

    // For simplicity in this "tab switching" fix:
    // We only return cached data effectively if we are reloading the component and not asking for new data explicitly?
    // Actually, the CommunityTab calls loadFeed(0, true) on mount. We should intercept that.

    if (!reset && skip === 0 && cache.communityFeed) {
      return cache.communityFeed;
    }

    try {
      const response = await axios.get(`${API}/entries/feed?skip=${skip}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      const newEntries = response.data;

      setCache(prev => {
        const currentFeed = prev.communityFeed || { entries: [], hasMore: true, page: 0 };
        let updatedEntries;

        if (reset || skip === 0) {
            updatedEntries = newEntries;
        } else {
            updatedEntries = [...currentFeed.entries, ...newEntries];
        }

        return {
          ...prev,
          communityFeed: {
            entries: updatedEntries,
            hasMore: newEntries.length >= limit,
            page: skip / limit // approximate page
          }
        };
      });

      return {
        entries: newEntries, // Return just the new ones for the caller to handle if they want localized state too
        fullFeed: reset || skip === 0 ? newEntries : [...(cache.communityFeed?.entries || []), ...newEntries]
      };
    } catch (error) {
      console.error('Failed to fetch community feed', error);
      throw error;
    }
  }, [auth.token, cache.communityFeed]);

  const fetchOverallLeaderboard = useCallback(async () => {
    if (cache.leaderboardOverall) return cache.leaderboardOverall;
    try {
      const response = await axios.get(`${API}/leaderboard/overall`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      updateCache('leaderboardOverall', response.data.leaderboard);
      return response.data.leaderboard;
    } catch (error) {
      console.error('Failed to fetch overall leaderboard', error);
      throw error;
    }
  }, [auth.token, cache.leaderboardOverall, updateCache]);

  const fetchMonthlyLeaderboard = useCallback(async (year, month) => {
    const key = `${year}-${month}`;
    if (cache.leaderboardMonthly[key]) return cache.leaderboardMonthly[key];
    try {
      const response = await axios.get(`${API}/leaderboard/month/${year}/${month}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setCache(prev => ({
        ...prev,
        leaderboardMonthly: { ...prev.leaderboardMonthly, [key]: response.data }
      }));
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch monthly leaderboard for ${year}-${month}`, error);
      throw error;
    }
  }, [auth.token, cache.leaderboardMonthly]);

  // -- Mutations (Actions that clear cache) --

  const refreshProfile = useCallback(() => {
    invalidate(['profileStats', 'calendarEntries']);
    // If I change my profile pic, it might affect feed/leaderboard, but maybe excessive to clear everything.
    // Let's stick to stats for now.
  }, [invalidate]);

  const recordEntryChange = useCallback(() => {
      // Major state change
      invalidate([
          'calendarEntries',
          'profileStats',
          'communityFeed',
          'leaderboardOverall',
          'leaderboardMonthly',
          'userProfiles' // My own user profile in someone else's view might be stale, but we can't easily fix that.
                         // We definitely should clear 'userProfiles' because if I view myself it might be cached.
      ]);
  }, [invalidate]);

  return (
    <CacheContext.Provider value={{
      cache,
      fetchProfileStats,
      fetchCalendarEntries,
      fetchCurrentChallenge,
      fetchChallengeForMonth,
      fetchCommunityFeed,
      fetchOverallLeaderboard,
      fetchMonthlyLeaderboard,
      invalidate,
      refreshProfile,
      recordEntryChange
    }}>
      {children}
    </CacheContext.Provider>
  );
};
